/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  IAnalyticsEvent,
  IPageData,
  TDigitalData,
  IPostOrderReturn,
  AnalyticsReturnItem,
} from '../models/analytics';
import { isSsr } from './general';

/**
 *  GENERIC ANALYTICS FUNCTIONS
 */

/**
 * Function for updating page data in the global variable _T
 * @param digitalData - the digital data object we want to push to page data array
 */
export const setAnalytics = (digitalData: TDigitalData) => {
  const exchange = window.digitalData?.user?.profile?.exchange;
  const _ddo = digitalData.init();
  if (_ddo?.user && exchange) {
    _ddo.user = {
      ..._ddo.user,
      profile: {
        ..._ddo.user.profile,
        exchange,
      },
    };
  }

  if (!isSsr) {
    window._T = window._T || {};

    if (typeof window._T.pageData === 'undefined') {
      window._T.pageData = [];
    }

    window._T.pageData.push(_ddo);
  }
};

/**
 * Function for updating an event array in the global variable digitalData
 * @param analyticsEvent - an event object we want to push to event array
 */
export const pushEventInfo = (analyticsEvent: IAnalyticsEvent) => {
  if (analyticsEvent && !isSsr && window.digitalData) {
    try {
      window.digitalData.event = window.digitalData.event || [];
      window.digitalData.event.push(analyticsEvent);
      setAnalytics(window.digitalData);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('DDO : Error in pushing the event info!!!');
    }
  }
};

/**
 * Function for setting page details in the global variable digitalData
 * @param page - an page details object we want to set in the digitalData
 */
export function setPageDetails(
  page: IPageData,
  extraFields: { postOrderReturn?: any; user?: any } = {},
) {
  const { postOrderReturn, user } = extraFields;
  if (!isSsr && window.digitalData) {
    try {
      window.digitalData.page = page;
      if (postOrderReturn) {
        window.digitalData.postOrderReturn = postOrderReturn;
      }
      if (user) {
        window.digitalData.user = {
          ...window.digitalData.user,
          profile: {
            ...window.digitalData.user?.profile,
            exchange: user?.profile?.exchange,
          },
        };
      }
      setAnalytics(window.digitalData);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(
        'DDO : Error in setting the page object and initializing it!!!',
      );
    }
  }
}

export const defaultPageInfo = {
  category: {
    primaryCategory: 'account',
    subCategory1: 'account>purchase history',
    subCategory2: 'account>purchase history>order details>return items',
  },
  pageInfo: {
    pageName: 'account>purchase history>order details>return items',
    pageType: 'maml: return items',
  },
};

export const setPageInfoWithEventCategories = (
  primaryCategory: string,
  subCategory1: string,
  subCategory2: string,
  pageName: string,
  pageType: string,
) => {
  const page: IPageData = {
    category: {
      primaryCategory,
      subCategory1,
      subCategory2,
    },
    pageInfo: {
      pageName,
      pageType,
    },
  };
  setPageDetails(page);
};

export const setPageInfoWithEvent = (
  primaryCategory: string,
  subCategory1: string,
  subCategory2: string,
  pageName: string,
  pageType: string,
  pageLoadEvent: string,
) => {
  const page: IPageData = {
    category: {
      primaryCategory,
      subCategory1,
      subCategory2,
    },
    pageInfo: {
      pageName,
      pageType,
      pageLoadEvent,
    },
  };

  setPageDetails(page);
};

export const sendReturnItemData = (postOrderReturn: IPostOrderReturn) => {
  if (!isSsr && !window.digitalData.postOrderReturn) {
    window.digitalData.postOrderReturn = { item: [] };
  }

  postOrderReturn?.item?.forEach((returnItem: AnalyticsReturnItem) => {
    if (!isSsr) window.digitalData.postOrderReturn.item.push(returnItem);
  });

  window.digitalData.postOrderReturn.transactionID =
    postOrderReturn.transactionID;
  setAnalytics(window.digitalData);
};
